import React from 'react'
import { Button, Select, TextInput } from 'components/Form'
import { SimpleTable } from 'components/Ui'
import { AiOutlineClose, AiOutlinePlus } from 'react-icons/ai'

import { lineItemDefaultData } from './store/dataSlice'

const LineItems = ({
    values,
    setFieldValue,
    errors,
    touched,
    accountCodes,
    taxTypes,
    handleLineItemAdd,
    handleLineItemRemove,
    handleLineItemUpdate,
}) => {
    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h4 className="text-lg font-medium">Invoice Line Items</h4>
                <Button
                    type="button"
                    onClick={e => {
                        e.preventDefault()
                        handleLineItemAdd(setFieldValue, values, accountCodes)
                    }}
                    className="flex items-center gap-2"
                    variant="twoTone"
                    color="green-600"
                    size="sm"
                >
                    <AiOutlinePlus size={16} />
                    Add Line Item
                </Button>
            </div>

            {values?.line_items?.length ? (
                <SimpleTable>
                    <thead>
                        <SimpleTable.TR>
                            <SimpleTable.TH>Description *</SimpleTable.TH>
                            <SimpleTable.TH>Quantity *</SimpleTable.TH>
                            <SimpleTable.TH>Unit Amount *</SimpleTable.TH>
                            <SimpleTable.TH>Account Code *</SimpleTable.TH>
                            <SimpleTable.TH>Tax Type *</SimpleTable.TH>
                            <SimpleTable.TH>Line Total</SimpleTable.TH>
                            <SimpleTable.TH>Action</SimpleTable.TH>
                        </SimpleTable.TR>
                    </thead>
                    <tbody>
                        {values.line_items.map((item, index) => (
                            <SimpleTable.TR key={`line-item-${index}`}>
                                <SimpleTable.TD>
                                    <TextInput
                                        type="text"
                                        placeholder="Description"
                                        value={item?.description || ''}
                                        onChange={e =>
                                            handleLineItemUpdate(setFieldValue, values, index, 'description', e.target.value)
                                        }
                                        error={
                                            errors.line_items?.[index]?.description && touched.line_items?.[index]?.description
                                        }
                                    />
                                </SimpleTable.TD>
                                <SimpleTable.TD>
                                    <TextInput
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        placeholder="Qty"
                                        value={item?.quantity || ''}
                                        onChange={e =>
                                            handleLineItemUpdate(setFieldValue, values, index, 'quantity', e.target.value)
                                        }
                                        error={errors.line_items?.[index]?.quantity && touched.line_items?.[index]?.quantity}
                                    />
                                </SimpleTable.TD>
                                <SimpleTable.TD>
                                    <TextInput
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder="Unit Amount"
                                        value={item?.unit_amount || ''}
                                        onChange={e =>
                                            handleLineItemUpdate(setFieldValue, values, index, 'unit_amount', e.target.value)
                                        }
                                        error={
                                            errors.line_items?.[index]?.unit_amount && touched.line_items?.[index]?.unit_amount
                                        }
                                    />
                                </SimpleTable.TD>
                                <SimpleTable.TD>
                                    <Select
                                        options={(() => {
                                            const uniqueCodes = {}
                                            // Add all account codes from the dropdown
                                            accountCodes.forEach(item => {
                                                uniqueCodes[item.value] = item
                                            })

                                            // Ensure the current line item's account code is included
                                            // even if it's not in the dropdown (for edit mode)
                                            const currentAccountCode = item?.account_code
                                            if (currentAccountCode && !uniqueCodes[currentAccountCode]) {
                                                // Create a temporary option for the current account code
                                                uniqueCodes[currentAccountCode] = {
                                                    value: currentAccountCode,
                                                    label: `${currentAccountCode} (from saved data)`,
                                                    data: { code: currentAccountCode, name: 'from saved data' },
                                                }
                                            }

                                            return Object.values(uniqueCodes)
                                        })()}
                                        placeholder="Account Code"
                                        value={item?.account_code || ''}
                                        onChange={e =>
                                            handleLineItemUpdate(setFieldValue, values, index, 'account_code', e.target.value)
                                        }
                                        error={
                                            errors.line_items?.[index]?.account_code && touched.line_items?.[index]?.account_code
                                        }
                                    />
                                </SimpleTable.TD>
                                <SimpleTable.TD>
                                    <Select
                                        options={taxTypes}
                                        placeholder="Tax Type"
                                        value={item?.tax_type || ''}
                                        onChange={e =>
                                            handleLineItemUpdate(setFieldValue, values, index, 'tax_type', e.target.value)
                                        }
                                        error={errors.line_items?.[index]?.tax_type && touched.line_items?.[index]?.tax_type}
                                    />
                                </SimpleTable.TD>
                                <SimpleTable.TD>
                                    <div className="font-medium">
                                        ${(parseFloat(item.quantity || 0) * parseFloat(item.unit_amount || 0)).toFixed(2)}
                                    </div>
                                </SimpleTable.TD>
                                <SimpleTable.TD>
                                    <div className="flex items-center gap-2">
                                        {values.line_items.length > 1 && (
                                            <Button
                                                type="button"
                                                onClick={e => {
                                                    e.preventDefault()
                                                    handleLineItemRemove(setFieldValue, values, index)
                                                }}
                                                variant="twoTone"
                                                color="red-600"
                                                size="sm"
                                                className="!px-2 !py-1"
                                            >
                                                <AiOutlineClose size={14} />
                                            </Button>
                                        )}
                                    </div>
                                </SimpleTable.TD>
                            </SimpleTable.TR>
                        ))}
                    </tbody>
                </SimpleTable>
            ) : (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400 border border-dashed rounded border-gray-300 dark:border-gray-600">
                    <p>No line items added yet.</p>
                    <Button
                        type="button"
                        onClick={e => {
                            e.preventDefault()
                            handleLineItemAdd(setFieldValue, values, accountCodes)
                        }}
                        variant="twoTone"
                        color="blue-600"
                        size="sm"
                        className="mt-2"
                    >
                        <AiOutlinePlus size={16} className="mr-2" />
                        Add First Line Item
                    </Button>
                </div>
            )}

            {values?.line_items?.length > 0 && (
                <div className="flex justify-end">
                    <div className="p-4 bg-gray-50 rounded">
                        <div className="text-lg font-medium">
                            Total: $
                            {values.line_items
                                .reduce((total, item) => {
                                    return total + parseFloat(item.quantity || 0) * parseFloat(item.unit_amount || 0)
                                }, 0)
                                .toFixed(2)}
                        </div>
                        <div className="text-sm text-gray-600">(Excluding tax)</div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default LineItems
